import { useMemo, useState } from 'react';
import { NJButton, NJSegmentedControl, NJSegmentedControlButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import Loading from '@shared/Loading';
import { NetworkShell, TEST_PUC_NETWORK, TOPOLOGY_PLANNING_ROWS, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { usePlanningMutations, usePlannings } from './hooks';
import PlanningPanel from './PlanningPanel';
import type { GanttRow, PlanningKind } from './types';

export const meta = {
    title: 'Référence — Réseau > Plannings',
    description:
        "Page de référence Plannings du réseau PUC EDM : plannings de production (sites → unités / pompes) et de topologie, Gantt CSS des contraintes, création de planning, ajout / suppression de contraintes, légende Nemo.",
    tags: ['planning', 'editor'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'plannings',
    createdAt: '2026-10-10',
};

const TABS: Array<{ id: PlanningKind; label: string }> = [
    { id: 'production', label: 'Production' },
    { id: 'topology', label: 'Topologie' },
];

export default function RefNetworkPlannings() {
    const nav = useSize2MixRefNavigation();
    const [tab, setTab] = useState<PlanningKind>('production');
    const { data, isLoading } = usePlannings();
    const { createPlanning } = usePlanningMutations(tab);

    const productionRows = useMemo<GanttRow[]>(() => [
        { id: 'biomass', label: 'Chaufferie biomasse', level: 0 },
        { id: 'biomass-boilers', label: 'Chaudière', level: 1 },
        { id: 'boiler-2', label: 'Chaudière 2', level: 2 },
        { id: 'heat-pumps', label: 'Pompe à chaleur', level: 1 },
        { id: 'test-v2', label: 'TEST V2', level: 2 },
        { id: 'biomass-geo', label: 'Unité de géothermie', level: 1 },
        { id: 'test', label: 'test', level: 2 },
        { id: 'biomass-pumps', label: 'Pompe de distribution', level: 1 },
        { id: 'biomass-pump', label: 'Pompes distrib biomasse', level: 2 },
        { id: 'gas', label: 'Chaufferie gaz', level: 0 },
        { id: 'gas-boilers', label: 'Chaudière', level: 1 },
        { id: 'gas-boiler', label: 'Chaudière gaz', level: 2 },
        { id: 'gas-geo', label: 'Unité de géothermie', level: 1 },
        { id: 'test-demo', label: 'test dmeo', level: 2 },
        { id: 'gas-pumps', label: 'Pompe de distribution', level: 1 },
        { id: 'gas-pump', label: 'Pompes existantes', level: 2 },
        { id: 'storages', label: "Stockages d'eau thermique", level: 0 },
        { id: 'water-storage', label: "Stockage d'eau", level: 1 },
        { id: 'storage', label: 'CH-1220004547', level: 2 },
    ], []);
    const topologyRows = useMemo<GanttRow[]>(() => {
        const targets = new Set((data?.topology ?? []).flatMap((p) => p.constraints.map((c) => c.target)));
        return TOPOLOGY_PLANNING_ROWS.flatMap((label) => [
            { id: label, label, level: 0 as const },
            ...(label === 'Fermeture groupe de canalisations' ? [...targets].map((t) => ({ id: t, label: t, level: 1 as const })) : []),
        ]);
    }, [data]);

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NetworkShell network={TEST_PUC_NETWORK} activePage="plannings" onNavigate={nav.onNetworkNavigate} onPrimaryNavigate={nav.onPrimaryNavigate}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0' }}>
                    <NJSegmentedControl label="Type de planning" size="md" value={tab} onChange={(value) => setTab(value as PlanningKind)}>
                        {TABS.map(({ id, label }) => <NJSegmentedControlButton key={id} value={id} label={label} />)}
                    </NJSegmentedControl>
                        <NJButton
                            label="Créer un nouveau planning"
                            emphasis="subtle"
                            scale="sm"
                            onClick={() => createPlanning.mutate(`Nouveau planning ${(data?.[tab].length ?? 0) + 1}`)}
                        />
                </div>
                <div style={{ padding: '16px 16px 0' }}>
                    {isLoading || !data ? (
                        <Loading text="Chargement des plannings" scale="md" />
                    ) : (
                        <PlanningPanel key={tab} kind={tab} plannings={data[tab]} rows={tab === 'production' ? productionRows : topologyRows} />
                    )}
                </div>
            </NetworkShell>
        </PrototypeStudioLayout>
    );
}
